import { useQuery } from "@tanstack/react-query";
import { ListCommanWay } from "API/commonApi";
import moment from "moment";
import React, { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import QRCode from "react-qr-code";
import "pages/AWB/awb_slip.css";
import Spinner from "utils/Spinner";

const getBoxSizeLabel = (data: any): string => {
  if (data.pkg1Charge == 1) return "1 LT";
  if (data.pkg2Charge == 1) return "4 LT";
  if (data.pkgData) {
    try {
      const parsed = typeof data.pkgData === "string" ? JSON.parse(data.pkgData) : data.pkgData;
      if (parsed?.boxSize) return parsed.boxSize;
    } catch {
      return data.pkgData;
    }
  }
  return "";
};

const PickupSlip = () => {
  const [searchParams] = useSearchParams();
  const pickupId = searchParams.get("id");

  const { data: pickupRes, isLoading } = useQuery(
    ["pickupSlipData", pickupId],
    () => ListCommanWay("/admin/pickup/list", { id: pickupId }, { paginate: 1 }),
    { enabled: !!pickupId, keepPreviousData: false }
  );

  const pickupData = pickupRes?.status === "SUCCESS" ? pickupRes?.data?.data?.[0] : null;

  useEffect(() => {
    if (pickupData) {
      setTimeout(() => window.print(), 600);
    }
  }, [pickupData]);

  if (!pickupId) return <p className="text-center mt-10 text-lg">No pickup ID provided</p>;
  if (isLoading) return <Spinner />;
  if (!pickupData) return <p className="text-center mt-10 text-lg">Pickup not found</p>;

  const code = pickupData.pickup_code || "";
  const boxSize = getBoxSizeLabel(pickupData);
  const tempType = pickupData.tempData || "";
  const pickupDate = pickupData.pickup_date_time
    ? moment(pickupData.pickup_date_time, "DD-MM-YYYY HH:mm:ss").format("DD/MM/YYYY")
    : "";

  return (
    <div style={{ padding: "0", margin: "0" }}>
          <div
            style={{
              width: "210mm",
              height: "153mm",
              margin: "0 auto",
              position: "relative",
              fontFamily: "Helvetica, sans-serif",
              fontStretch: "condensed",
              lineHeight: "normal",
            }}
          >
            {/* Watermark */}
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "157mm",
                backgroundImage: 'url("https://ats.airfield.express/logo3.png")',
                backgroundSize: "35mm",
                opacity: "0.05",
                pointerEvents: "none",
              }}
            />

            <div className="flex border-black" style={{ height: "100%", justifyContent: "space-between" }}>
              {/* Left main area */}
              <div
                style={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  width: "160mm",
                }}
              >
                {/* Header */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    borderBottom: "1px solid black",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", margin: "0.3mm" }}>
                    <img
                      src="data:image/png;base64,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"
                      style={{ height: "12mm" }}
                    />
                    <img src="https://ats.airfield.express/logo3.png" style={{ height: "12.2mm" }} />
                  </div>
                  <div>PICKUP SLIP</div>
                  <div
                    style={{
                      backgroundColor: "#034da2",
                      color: "#fff",
                      height: "12.2mm",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      marginRight: "0.25mm",
                      padding: "0 6mm",
                    }}
                  >
                    <p>
                      <b>{pickupData.product_name || "PICKUP"}</b>
                    </p>
                  </div>
                </div>

                {/* Shipper / Consignee row */}
                <div className="flex" style={{}}>
                  <div style={{ flex: 1 }}>
                    <div className="flex" style={{ height: "100%", width: "100%" }}>
                      <div
                        style={{
                          backgroundColor: "#034da2",
                          padding: "2mm 0.2mm",
                          color: "#fff",
                          display: "inline-block",
                          writingMode: "vertical-rl",
                          transform: "rotate(180deg)",
                          fontSize: "3.2mm",
                          letterSpacing: "0.25mm",
                        }}
                      >
                        <b>SHIPPER</b>
                      </div>
                      <div style={{ margin: "1mm 2mm", flex: 1 }}>
                        <div style={{ display: "flex" }}>
                          <p>Client Code:</p>
                          <p style={{ flex: 1, borderBottom: "1px solid slategray" }}>{pickupData.customer_code || ""}</p>
                        </div>
                        <div style={{ display: "flex" }}>
                          <p>Company:</p>
                          <p style={{ flex: 1, borderBottom: "1px solid slategray" }}>{pickupData.shipper_company || ""}</p>
                        </div>
                        <div style={{ display: "flex" }}>
                          <p>Sender: </p>
                          <p style={{ flex: 1, borderBottom: "1px solid slategray" }}>{pickupData.shipper_sh || ""}</p>
                        </div>
                        <div style={{ display: "flex" }}>
                          <p>Address:</p>
                          <p style={{ flex: 1, borderBottom: "1px solid slategray" }}>
                            <small>{pickupData.shipper_address || ""}</small>
                          </p>
                        </div>
                        <div style={{ display: "flex" }}>
                          <p>City:</p>
                          <p style={{ flex: 2, borderBottom: "1px solid slategray" }}>{pickupData.shipper_city || ""}</p>
                          <p>Pin:</p>
                          <p style={{ flex: 1, borderBottom: "1px solid slategray" }}>{pickupData.shipper_pincode || ""}</p>
                        </div>
                        <div style={{ display: "flex" }}>
                          <p>Eway Bill No.:</p>
                          <p style={{ flex: 1, borderBottom: "1px solid slategray" }}></p>
                        </div>
                        <div style={{ display: "flex" }}>
                          <p>GST:</p>
                          <p style={{ flex: 1, borderBottom: "1px solid slategray" }}>{pickupData.shipper_gst_no || ""}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div style={{ flex: 1, borderLeft: "1px solid #fff" }}>
                    <div className="flex" style={{ height: "100%" }}>
                      <div
                        style={{
                          backgroundColor: "#034da2",
                          padding: "2mm 0.2mm",
                          color: "#fff",
                          display: "inline-block",
                          writingMode: "vertical-rl",
                          transform: "rotate(180deg)",
                          fontSize: "3.2mm",
                          letterSpacing: "0.25mm",
                        }}
                      >
                        <b>CONSIGNEE</b>
                      </div>
                      <div style={{ margin: "1mm 2mm", flex: 1 }}>
                        <div style={{ display: "flex" }}>
                          <p>Attn.:</p>
                          <p style={{ flex: 1, borderBottom: "1px solid slategray" }}>{pickupData.consignee_cgnee || ""}</p>
                        </div>
                        <div style={{ display: "flex" }}>
                          <p>Company:</p>
                          <p style={{ flex: 1, borderBottom: "1px solid slategray" }}>{pickupData.consignee_company || ""}</p>
                        </div>
                        <div style={{ display: "flex" }}>
                          <p>Tel: </p>
                          <p style={{ flex: 1, borderBottom: "1px solid slategray" }}>{pickupData.consignee_mobile || ""}</p>
                        </div>
                        <div style={{ display: "flex" }}>
                          <p>Address: </p>
                          <p style={{ flex: 1, borderBottom: "1px solid slategray" }}>
                            <small>{pickupData.consignee_address || ""}</small>
                          </p>
                        </div>
                        <div style={{ display: "flex" }}>
                          <p>City:</p>
                          <p style={{ flex: 2, borderBottom: "1px solid slategray" }}>{pickupData.consignee_city || ""}</p>
                          <p>Pin:</p>
                          <p style={{ flex: 1, borderBottom: "1px solid slategray" }}>{pickupData.consignee_pincode || ""}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Bottom section: disclaimers, barcode, details, delivery */}
                <div className="flex" style={{ flex: 1 }}>
                  {/* Disclaimers */}
                  <div style={{ flex: 1, minWidth: "48mm", maxWidth: "48mm" }}>
                    <div style={{ fontSize: "2.6mm" }}>
                      <p style={{ padding: "0.5mm", borderBottom: "1px solid black", borderTop: "1px solid black" }}>
                        Any Customs, Gst, Sales Tax, Duties or Taxes as may be applicable on this shipment will be paid by the Consignee at
                        the time of delivery of the shipment. AIRFIELD EXPRESS reserves the right of lien on any shipment till all its dues
                        are paid in full in respect of Freight, Customs, Gst, Taxes and other charges.
                      </p>
                      <p style={{ padding: "0.5mm", borderBottom: "1px solid black" }}>
                        "In case this consignment contains anything of value, the company recommends insurance of the same. The company's
                        liability on this shipment is limited to Rs. 2,500/- or cost of reconstruction whichever is lower."
                      </p>
                      <p style={{ padding: "0.5mm", borderBottom: "1px solid black" }}>
                        I hereby agree to the terms and conditions set forth on the reverse this (Consignor) copy of this non- negotiable way
                        bill and warrant that the information contained on this way bill is true and correct and this Consignment does not
                        contain any
                      </p>
                      <p style={{ padding: "0.5mm", borderBottom: "1px solid black" }}>
                        If Consignee fails to make any such payment(s) AIRFIELD EXPRESS shall be entitled to recover the same from the
                        Consignor. Freight charge and Service tax shall be paid by the Consignor. Any Government duty if chargeable on the
                        waybill shall be borne by the Consignor exclusively.
                      </p>
                    </div>
                    <div style={{ fontSize: "3mm" }}>
                      <p>Name: {pickupData.shipper_sh || ""}</p>
                      <p>Shipper's Sign: _________</p>
                    </div>
                  </div>

                  {/* Middle: Particulars, QR, info */}
                  <div
                    style={{
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                      borderLeft: "1px solid black",
                    }}
                  >
                    <div className="flex" style={{ borderBottom: "1px solid black" }}>
                      <div style={{ flex: "1.75", fontSize: "3.2mm" }}>
                        <div
                          style={{
                            backgroundColor: "#034da2",
                            color: "#fff",
                            height: "7mm",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            borderRight: "1px solid white",
                          }}
                        >
                          <p>
                            <b>Particulars</b>
                          </p>
                        </div>
                        <p style={{ borderRight: "1px solid black", padding: "0.5mm" }}>&nbsp;</p>
                      </div>
                      <div style={{ flex: 1, fontSize: "3.2mm" }}>
                        <div
                          style={{
                            backgroundColor: "#034da2",
                            color: "#fff",
                            height: "7mm",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            textAlign: "center",
                            borderRight: "1px solid white",
                          }}
                        >
                          <p>
                            <b>
                              Commercial
                              <br />
                              Value
                            </b>
                          </p>
                        </div>
                        <p style={{ padding: "0.5mm" }}></p>
                      </div>
                    </div>

                    {/* QR Code */}
                    <div
                      style={{
                        flex: 1,
                        alignItems: "center",
                        justifyContent: "center",
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      <div
                        style={{
                          position: "relative",
                          backgroundColor: "#fff",
                          width: "90%",
                          height: "95%",
                          display: "flex",
                          flexDirection: "column",
                          gap: 5,
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <QRCode value={code || ""} size={105} />
                        <p style={{ margin: "0", fontSize: 20, fontWeight: 500, fontFamily: "monospace", letterSpacing: 1 }}>{code}</p>
                      </div>
                    </div>

                    {/* Weight & info */}
                    <div>
                      <div
                        style={{
                          fontSize: "3mm",
                          padding: "1.5mm 1mm",
                          display: "flex",
                          justifyContent: "space-between",
                          borderTop: "1px solid black",
                        }}
                      >
                        <p style={{ flex: 1 }}>
                          Act. Wt. (Kg.): <b>{pickupData.weight || ""}</b>
                        </p>
                        <p style={{ flex: 1 }}>Dim. Wt. (Kg.):</p>
                      </div>
                      <div
                        style={{
                          fontSize: "3mm",
                          padding: "1mm",
                          display: "flex",
                          justifyContent: "space-between",
                          borderTop: "1px solid black",
                        }}
                      >
                        <p>No Of Piece.: {pickupData.quantity || ""}</p>
                        <p>ORIGIN: {pickupData.shipper_city || ""}</p>
                        <p style={{ width: "20mm" }}>DEST.: {pickupData.consignee_city || ""}</p>
                      </div>
                      <p
                        style={{
                          backgroundColor: "#034da2",
                          padding: "2mm 0",
                          color: "white",
                          fontSize: "2.3mm",
                          textAlign: "center",
                        }}
                      >
                        <b>PLEASE QUOTE THE ABOVE PICKUP CODE FOR FUTURE CORRESPONDENCE</b>
                      </p>
                      <div className="flex">
                        <div
                          style={{
                            fontSize: "2.8mm",
                            padding: "1mm",
                            borderRight: "1px solid black",
                            flex: 1,
                            lineHeight: "5mm",
                          }}
                        >
                          <p>
                            P/U Date: <b>{pickupDate}</b>
                          </p>
                          <p>P/U Time:</p>
                          <p>Emp.#:</p>
                          <p>
                            CASH/Credit: <u></u>
                          </p>
                          <p>
                            W/I or P/U: <u><b>P/U</b></u>
                          </p>
                        </div>
                        <div style={{ fontSize: "2.8mm", padding: "1mm", flex: "1.6" }}>
                          <p>
                            The Consigner has stated that:
                            <br />
                            ▢ Has not insured the Consignment
                            <br />
                            ▢ Has insured the Consignment
                            <br />
                            &nbsp;
                          </p>
                          <div style={{ lineHeight: "5mm" }}>
                            <p>
                              Insurance Policy No: <u></u>
                            </p>
                            <p>
                              Amount: <u></u>
                            </p>
                            <p>
                              Insurance Company: <u></u>
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Delivery column */}
                  <div
                    style={{
                      borderLeft: "1px solid black",
                      flex: 1,
                      minWidth: "30mm",
                      maxWidth: "40mm",
                    }}
                  >
                    <div
                      style={{
                        backgroundColor: "#034da2",
                        color: "#fff",
                        height: "7mm",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "3.2mm",
                      }}
                    >
                      <p>
                        <b>Delivery</b>
                      </p>
                    </div>
                    <div style={{ fontSize: "3mm" }}>
                      <div style={{ padding: "1mm", lineHeight: "6mm" }}>
                        <p>Del. Date:</p>
                        <p>Name:</p>
                        <p>Time:</p>
                      </div>
                      <p
                        style={{
                          padding: "0.5mm",
                          borderBottom: "1px solid black",
                          borderTop: "1px solid black",
                          fontSize: "2.8mm",
                        }}
                      >
                        Received above shipment in good condition. We agree to pay all charges including customes duties, taxes &amp; Gst as
                        applicable.
                      </p>
                      <div style={{ padding: "1mm" }}>
                        <p>Consignee's</p>
                        <p style={{ marginTop: "3mm" }}>
                          Name: <u>{pickupData.consignee_cgnee || ""}</u>
                        </p>
                        <p style={{ marginTop: "6mm" }}>Signature</p>
                        <p style={{ marginTop: "6mm" }}>Relative Receiving</p>
                        <p style={{ marginTop: "6mm" }}>Company Stamp</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right side: Temp Type & Pkg Size (instead of charges) */}
              <div
                style={{
                  width: "50mm",
                  height: "100%",
                  display: "flex",
                  borderLeft: "1px solid black",
                }}
              >
                <div
                  style={{
                    width: "46mm",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    borderRight: "1px solid black",
                  }}
                >
                  <div
                    style={{
                      backgroundColor: "rgb(231, 231, 231)",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "space-between",
                      flex: 1,
                    }}
                  >
                    <table
                      style={{
                        borderCollapse: "collapse",
                        width: "100%",
                        fontSize: "3.2mm",
                      }}
                    >
                      <tbody>
                        <tr style={{ borderBottom: "1px solid black", borderTop: "2px solid black" }}>
                          <td style={{ padding: "2mm" }}>
                            <b>Temp Range:</b>
                          </td>
                          <td style={{ textAlign: "right", paddingRight: "1.5mm", fontSize: "3.5mm" }}>
                            <b>{tempType}</b>
                          </td>
                        </tr>
                        <tr style={{ borderBottom: "1px solid black" }}>
                          <td style={{ padding: "2mm" }}>
                            <b>Pkg Size:</b>
                          </td>
                          <td style={{ textAlign: "right", paddingRight: "1.5mm", fontSize: "3.5mm" }}>
                            <b>{boxSize}</b>
                          </td>
                        </tr>
                        <tr style={{ borderBottom: "1px solid black" }}>
                          <td style={{ padding: "2mm" }}>
                            <b>Type:</b>
                          </td>
                          <td style={{ textAlign: "right", paddingRight: "1.5mm", fontSize: "3.5mm" }}>
                            <b>{pickupData.type || ""}</b>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                    <div style={{ flex: 1 }}>
                      <div style={{ padding: "1mm 0 0 1mm" }}>Remarks</div>
                      <div style={{ padding: "1mm", fontSize: "3mm" }}></div>
                    </div>
                  </div>
                </div>
                <div style={{ width: "4mm" }}>
                  <p
                    style={{
                      transform: "rotate(90deg)",
                      transformOrigin: "bottom left",
                      height: "4.5mm",
                      whiteSpace: "nowrap",
                      marginRight: "1mm",
                    }}
                  >
                    <b>F-OP:01/REV00/1 12 16</b>
                  </p>
                </div>
              </div>
            </div>

            {/* Bottom bar */}
            <div style={{ height: "4mm", display: "flex", alignItems: "center" }} className="border-black">
              <div
                style={{
                  backgroundColor: "#034da2",
                  color: "#fff",
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "3mm",
                  padding: "0 1mm",
                }}
              >
                <p>
                  <b>NON INSURED SHIPMENT</b>
                </p>
              </div>
              <div
                style={{
                  backgroundColor: "#034da2",
                  color: "#fff",
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "3mm",
                  padding: "0 1mm",
                  borderLeft: "1px solid white",
                }}
              >
                <p>
                  <b>NON NEGOTIABLE OWNER'S RISK</b>
                </p>
              </div>
              <p
                style={{
                  flex: 1,
                  textAlign: "center",
                  color: "#034da2",
                  fontSize: "3.5mm",
                }}
              >
                <b>Track this pickup at airfield.co.in &middot; Call us: 9227223131 &middot; Transporter Id: 24AALCA0600L1Z0</b>
              </p>
            </div>
          </div>
    </div>
  );
};

export default PickupSlip;
